import Template from './Template';
import { isAttributeChangeObservable } from '../model/Model';
import { setup } from '../setup/Setup';


export interface ViewConstructor {
	new( config:ViewConfig ):View;
}

export interface ViewDefinition<TData = unknown> {
	template:string;
	registrations?:ViewRegistrations;
	getData?():TData;
	init?( this: View<TData> ):void;
	update?( this:View<TData>, name?:string ):void;
}

export interface ViewFactory {
	():View;
}

export interface ViewRegistrations {
	[ name:string ]:ViewConstructor|ViewDefinition|ViewFactory;
}

function convertToViewFactory( registration:ViewConstructor|ViewDefinition|ViewFactory ):ViewFactory {

	if( isViewDefinition( registration ) ) {
		const viewClass = class extends View {};
		if( registration.init ) {
			viewClass.prototype['init'] = registration.init;
		}
		if( registration.update ) {
			viewClass.prototype['update'] = registration.update;
		}
		return () => new viewClass({ ...registration, data:registration.getData && registration.getData() });
	}
	else if( isViewConstructor( registration ) ) {
		return () => new registration({});
	}
	else {
		return registration;
	}

	function isViewDefinition( registration:ViewConstructor|ViewDefinition|ViewFactory ):registration is ViewDefinition {
		return typeof registration === 'object';
	}

	function isViewConstructor( registration:ViewConstructor|ViewFactory ):registration is ViewConstructor {
		for ( let proto = Object.getPrototypeOf( registration ); proto; proto = Object.getPrototypeOf( proto ) ) {
			if( proto === View ) {
				return true;
			}
		}
		return false;
	}
}


const registrations:ViewRegistrations = {};


/**
 * Deprecated. Use `register` instead.
 * @deprecated */
export const ioc = new class {

	/** Deprecated. Use `ioc[name] = factory` instead.
	 * @deprecated
	 */
	public add( name:string, factory:ViewFactory ):void {
		registrations[ name ] = factory;
	}

	/** Deprecated. Use `ioc[name]` instead.
	 * @deprecated
	*/
	public has( name:string ):boolean {
		return name in registrations;
	}

	/** Deprecated. Use `name in ioc` instead.
	 * @deprecated
	*/
	public get( name:string ):ViewConstructor|ViewDefinition|ViewFactory {
		return registrations[ name ];
	}
};


/**
 * Registers the `View` class specified by `constructor` under the given `name`
 * such that it can be instantiated in templates using e.g. `<div view="name"></div>`.
 *
 * ### Example:
 *
 * @example
```typescript
  class Foo extends View { ... }
  register('Foo', Foo);
```
 *
 * Once thus registered, you will be able to instantiate `Foo` in templates e.g. as follows:
 *
```html
  <div view="Foo"></div>
```
 *
 * @param name The registration name which should be associated with the given `View` class.
 * @param constructorOrFactory The `View` class to associate with `name`, or a parameterless closure (of type `ViewFactory`) that creates a `View` instance.
 */
export function register( name:string, constructorOrFactory:ViewConstructor|ViewFactory );

/**
 * Registers an ad-hoc `View` class created from the specified `definition` under the given `name`
 * such that it can be instantiated in templates using e.g. `<div view="name"></div>`.
 *
 * For simple views, this overload can spare you the work of writing a full-blown class.
 *
 * ### Example:
 *
 * @example
```typescript
  register('Foo', {
      template: `<div>Greetings, [[name]].</div>`,
      data: {
          name: 'stranger'
      }
  });
```
 *
 * Once thus registered, you will be able to instantiate `Foo` in templates e.g. as follows:
 *
```html
  <div view="Foo"></div>
```
 *
 * ### Remarks:
 *
 * Note that data binding will not work for the above component because `data` is a plain object
 * whose properties will not dispatch `change` signals. If you want data binding to work,
 * set `data` to an instance of `Model` instead of a plain object.
 *
 * @param name The registration name which should be associated with the ad-hoc `View` class.
 * @param definition Definition of the ad-hoc `View` class. You must specify at least its `template`.
 */
export function register<TData = unknown>( name:string, definition:ViewDefinition<TData> );

/**
 * Registers the specified view registrations.
 *
 * @param registrations The registrations to register.
 */
export function register( registrations:ViewRegistrations ):void;

export function register<TData = unknown>( nameOrRegistrations:string|ViewRegistrations, registration?:ViewConstructor|ViewDefinition<TData>|ViewFactory ) {
	if( typeof nameOrRegistrations === 'string' ) {
		registrations[nameOrRegistrations] = convertToViewFactory( registration );
	}
	else {
		for( const name in nameOrRegistrations ) {
			const registration = nameOrRegistrations[ name ];
			if( typeof registration === 'object' ) {
				register<unknown>( name, registration );
			}
			else {
				register(name, registration );
			}
		}
	}
}

export function scan( root:Element, registrations?:ViewRegistrations ) {
	var temp = new View( { data:setup.data(), registrations } );
	temp.render( root );
}

export interface ViewConfig<TData = {}> {
	data?:TData,
	registrations?:ViewRegistrations,
	template?:string
}

/**
 * A common view class that has a container to render content/template into.
 */
export class View<TData = any> {

	protected _data:TData;

	public template:Template;
	public parent:View<{}>;
	public readonly registrations:ViewRegistrations;

	constructor( config:ViewConfig<TData> ) {

		this.registrations = config.registrations;

		this.template = new Template( config.template );
		this.template.viewFactory = this.createChild.bind( this );
		this.template.dataFactory = () => this._data;

		this._data = config.data;
	}


	/**
	 * Renders the view template.
	 * If an empty target is given, the template is rendered into to given target.
	 * If the target is not empty, the content of the target is used as the template.
	 */
	public render( target?:Element ) {

		this.template.render( target );

		// register databinding if model is given
		if( isAttributeChangeObservable( this.data ) ) {
			this.data.change.subscribe( ( change ) => {
				this.update( change.name );
			});
		}

		// everything is ready now we can invoke view's init
		this.init();
	}

	/**
	 * The init function is automatically called after the render function.
	 * So everything is rendered and the node is ready to be used.
	 * Overwrite the init function for custom dom manipulations.
	 */
	protected init() {

	}


	/**
	 * Returns the root node of the view
	 */
	public get node():Element {
		return this.template.root;
	}

	/**
	 * Updates the view if data attribute has changed
	 */
	public update( name?:string ) {
		this.template.update( this._data, name );
	}

	public child<T = View>( name:string ):T {
		return this.template.first( name ) as T;
	}

	public createChild( type:string ) {
		var view = convertToViewFactory( this.resolve( type ) )();
		view.parent = this;
		return view;
	}

	set data( data:TData ) {
		this._data = data;
	}
	get data():TData {
		return this._data;
	}

	private resolve( name:string ):ViewConstructor|ViewDefinition|ViewFactory {
		if( this.registrations && name in this.registrations ) {
			return this.registrations[ name ];
		}
		else if( this.parent ) {
			return this.parent.resolve( name );
		}
		else if( name in registrations ) {
			return registrations[ name ];
		}
		else if( name !== 'default' ) {
			return this.resolve( 'default' );
		}
		else {
			throw new Error( `There is no view registration named '${name}', nor is there a 'default' fallback.` );
		}
	}
}

export default View;
